import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { Observable } from 'rxjs';
import { ActiveStatusEnum } from '@models';

// PascalCase throughout — the API's Newtonsoft config keeps C# property casing on the wire.
// Breeze entities are lowercase, but these are raw controller output. Camel-casing a field here
// silently yields undefined rather than an error.
//
// There is no account or program picker here on purpose: the board lives inside the Program
// context, which already has both in scope.

export interface PugTransferTileDto {
  Id: string;
  Name: string;
  ActiveStatusId: ActiveStatusEnum;
  MemberCount: number;
  PurchaseOrder: string | null;
  AllowanceName: string | null;
  AllowanceAmt: number | null;
  IssuanceCount: number;
  OrderTimeFrameStartDays: number | null;
  OrderTimeFrameDurationDays: number | null;
  /** Non-null when the tile cannot accept a drop at all. Reserved for the genuinely impossible. */
  CannotReceiveReason: string | null;
  /** Non-null when the drop is allowed but has a consequence worth stating first. */
  WarningReason: string | null;
}

export interface PugTransferUserDto {
  Id: string;
  /** "Last, First" — the roster is read and sorted by surname. */
  Name: string;
  FirstName: string;
  LastName: string;
  CurrentPugId: string | null;
  CurrentPugName: string | null;
  /** Non-null when the user cannot be dragged, naming the blocking orders. */
  LockedReason: string | null;
}

export interface PugTransferBoardDto {
  AccountId: string;
  ProgramId: string;
  ProgramName: string;
  ProgramStartDate: string | null;
  ProgramEndDate: string | null;
  /** Non-null when this program cannot host a transfer at all. Tiles and Users are then empty. */
  DisqualifiedReason: string | null;
  Tiles: PugTransferTileDto[];
  Users: PugTransferUserDto[];
}

export interface PugTransferRequestDto {
  AccountId: string;
  ProgramId: string;
  TargetProgramUserGroupId: string;
  AccountUserIds: string[];
}

export interface PugTransferResultDto {
  UsersTransferred: number;
  UsersSkipped: number;
  ExclusionsWritten: number;
  ExclusionsRemoved: number;
  MembershipsAdded: number;
  AllowanceAdjustments: number;
  AllowanceAmtCarried: number;
  IssuanceAdjustments: number;
  OrdersRepointed: number;
  /** Collected orders sent back to Setup because the target group's batch has already been released. */
  OrdersReturnedToSetup: number;
  TargetPugName: string;
  /** Who actually moved, "Last, First". */
  TransferredUserNames: string[];
  /** Ids of those who moved, so the board can badge them for the session. */
  TransferredUserIds: string[];
  Problems: string[];
}

@Injectable({ providedIn: 'root' })
export class PugTransferApiService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}Account`;

  getBoard(accountId: string, programId: string): Observable<PugTransferBoardDto> {
    const params = new HttpParams().set('accountId', accountId).set('programId', programId);
    return this.http.get<PugTransferBoardDto>(`${this.baseUrl}/GetPugTransferBoard`, { params });
  }

  transfer(request: PugTransferRequestDto): Observable<PugTransferResultDto> {
    return this.http.post<PugTransferResultDto>(`${this.baseUrl}/TransferAccountUsersToPug`, request);
  }
}
